/**
 * Footer Accordion Component
 * Provides collapsible footer menus on mobile, always open on desktop
 */
class FooterAccordion extends HTMLElement {
  constructor() {
    super();
    this.detailsElement = this.querySelector('details');
    this.summaryElement = this.querySelector('summary');

    // Bind methods to maintain context
    this.onSummaryClick = this.onSummaryClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.handleMatchSmall = this.handleMatchSmall.bind(this);
    this.handleUnmatchSmall = this.handleUnmatchSmall.bind(this);
  }

  connectedCallback() {
    if (!this.detailsElement || !this.summaryElement) return;

    // Set up event listeners
    this.summaryElement.addEventListener('click', this.onSummaryClick);
    this.summaryElement.addEventListener('keyup', this.onKeyUp);

    // Set up media query for responsive behavior
    this.mediaQuery = window.matchMedia('(max-width: 767px)');
    this.handleMediaChange(this.mediaQuery);

    // Listen for changes
    this.mediaQuery.addEventListener('change', (e) => this.handleMediaChange(e));

    // Check if summary is empty
    this.checkEmptyState();
  }

  disconnectedCallback() {
    if (this.summaryElement) {
      this.summaryElement.removeEventListener('click', this.onSummaryClick);
      this.summaryElement.removeEventListener('keyup', this.onKeyUp);
    }

    if (this.mediaQuery) {
      this.mediaQuery.removeEventListener('change', this.handleMediaChange);
    }
  }

  /**
   * Handle media query changes
   */
  handleMediaChange(e) {
    if (e.matches) {
      // Mobile view - allow collapse
      this.handleMatchSmall();
    } else {
      // Desktop view - always open
      this.handleUnmatchSmall();
    }
  }

  /**
   * Mobile breakpoint matched (≤767px)
   */
  handleMatchSmall() {
    this.setAttribute('data-mobile', '');
    // On mobile, let user control open/close state
    // Start closed by default
    if (!this.hasAttribute('data-keep-open')) {
      this.close();
    }
  }

  /**
   * Desktop breakpoint (>767px)
   */
  handleUnmatchSmall() {
    this.removeAttribute('data-mobile');
    // On desktop, always keep open
    this.open();
  }

  /**
   * Handle summary click
   */
  onSummaryClick(event) {
    // Only toggle on mobile
    if (this.hasAttribute('data-mobile')) {
      event.preventDefault();
      if (this.detailsElement.hasAttribute('open')) {
        this.close();
      } else {
        this.open();
      }
    }
  }

  /**
   * Handle keyboard navigation
   */
  onKeyUp(event) {
    if (event.code === 'Escape' && this.hasAttribute('data-mobile')) {
      this.close();
    }
  }

  /**
   * Open the accordion
   */
  open() {
    this.detailsElement.setAttribute('open', '');
    this.summaryElement.setAttribute('aria-expanded', 'true');
  }

  /**
   * Close the accordion
   */
  close() {
    // Don't close on desktop or if marked to keep open
    if (!this.hasAttribute('data-mobile')) {
      return;
    }

    this.detailsElement.removeAttribute('open');
    this.summaryElement.setAttribute('aria-expanded', 'false');
  }

  /**
   * Check if summary content is empty and add class if needed
   */
  checkEmptyState() {
    if (this.summaryElement && !this.summaryElement.textContent.trim()) {
      this.summaryElement.classList.add('footer-accordion__summary--empty');
    }
  }
}

// Register the custom element
customElements.define('footer-accordion', FooterAccordion);
